import { memo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useQuery } from '@apollo/client';
import { NetProfileAuthDocument } from '@/graphql/generated/net-profile-auth.generated';

/**
 * Compact network disambiguator for Andie tool cards. When a tool runs once per
 * social network (e.g. planning for several profiles), this lets the user tell
 * which card belongs to which network.
 *
 * - `network`: a raw network code/name already present in the tool args.
 * - `privateProfileId`: a NetProfileAuth id that we resolve to its network name
 *   with a single-profile lookup.
 */
const AndieToolNetworkChip = memo(
    ({ network, privateProfileId }: { network?: string; privateProfileId?: string }) => {
        const needsLookup = !network && !!privateProfileId;

        const { data } = useQuery(NetProfileAuthDocument, {
            skip: !needsLookup,
            variables: { id: privateProfileId ?? '' },
            fetchPolicy: 'cache-first',
        });

        let label: string | undefined;
        let color: string | undefined;

        if (needsLookup) {
            label = data?.netProfileAuth?.netProfile?.net?.name;
            color = data?.netProfileAuth?.netProfile?.net?.color;
        } else if (network) {
            label = network.charAt(0).toUpperCase() + network.slice(1);
        }

        if (!label) return null;

        return (
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                <Box
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        flexShrink: 0,
                        bgcolor: color || 'text.disabled',
                    }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>
                    {label}
                </Typography>
            </Stack>
        );
    },
);

AndieToolNetworkChip.displayName = 'AndieToolNetworkChip';
export default AndieToolNetworkChip;
